import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { isBundledMobileApp } from '@/mobile/native/apiBase';
import { mobileApiFetch } from '@/mobile/native/mobileApiFetch';
import { loadFieldCache } from '@/mobile/sync/fieldSyncCache';
import {
  collectAllMobilePrefetchPaths,
  DEFAULT_MOBILE_PREFETCH_LIMITS,
  isMobileOfflinePath,
  type MobilePrefetchLimits,
} from '@/mobile/offline/mobileOfflineRoutes';

const INERTIA_HEADERS: Record<string, string> = {
  'X-Inertia': 'true',
  'X-Requested-With': 'XMLHttpRequest',
  Accept: 'text/html, application/xhtml+xml',
};

async function prefetchOne(path: string): Promise<void> {
  const response = isBundledMobileApp()
    ? await mobileApiFetch(path, {
        method: 'GET',
        headers: INERTIA_HEADERS,
      })
    : await fetch(path, {
        method: 'GET',
        credentials: 'include',
        headers: INERTIA_HEADERS,
      });

  if (import.meta.env.DEV && !response.ok) {
    console.warn(`[prefetch] ${path} → ${response.status}`);
  }
}

/** Prefetch em lotes para não sobrecarregar o servidor. */
export async function prefetchInertiaPaths(
  paths: string[],
  options?: { concurrency?: number },
): Promise<void> {
  const concurrency = options?.concurrency ?? 4;
  const unique = [...new Set(paths)].filter((path) =>
    isMobileOfflinePath(new URL(path, 'http://local').pathname),
  );

  for (let i = 0; i < unique.length; i += concurrency) {
    const batch = unique.slice(i, i + concurrency);
    await Promise.all(
      batch.map(async (path) => {
        try {
          await prefetchOne(path);
        } catch (error) {
          if (import.meta.env.DEV) {
            console.warn(`[prefetch] falhou ${path}`, error);
          }
        }
      }),
    );
  }
}

/**
 * Pré-carrega rotas estáticas do mobile + detalhes derivados do sync (relatórios/obras).
 */
export async function prefetchFieldInertiaPages(options?: {
  limits?: MobilePrefetchLimits;
}): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  if (!window.isSecureContext) {
    return;
  }

  const status = await Network.getStatus();
  if (!status.connected) {
    return;
  }

  const cache = await loadFieldCache();
  const limits = options?.limits ?? DEFAULT_MOBILE_PREFETCH_LIMITS;
  const paths = collectAllMobilePrefetchPaths(cache, { limits });

  if (import.meta.env.DEV) {
    console.info(`[prefetch] ${paths.length} rotas mobile`);
  }

  await prefetchInertiaPaths(paths);
}

/** Guarda no cache a rota que o utilizador acabou de abrir (com rede). */
export async function prefetchCurrentMobilePage(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !window.isSecureContext) {
    return;
  }

  const status = await Network.getStatus();
  if (!status.connected) {
    return;
  }

  const path = `${window.location.pathname}${window.location.search}`;

  if (!isMobileOfflinePath(window.location.pathname)) {
    return;
  }

  await prefetchInertiaPaths([path], { concurrency: 1 });
}
